import React, { useState, useEffect } from 'react';
import { usePayment, PaymentMethod } from './PaymentProvider';
import StripePayment from './StripePayment';
import PayPalPayment from './PayPalPayment';
import './PaymentForm.css';

interface PaymentFormProps {
  bookingId: string;
  amount: number;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const PaymentForm: React.FC<PaymentFormProps> = ({
  bookingId,
  amount,
  onSuccess,
  onCancel
}) => {
  const { initializePayment, processPayment, isProcessing, error, clearError } = usePayment();
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null);
  const [paymentIntent, setPaymentIntent] = useState<any>(null);
  const [showMethodSelection, setShowMethodSelection] = useState(true);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => clearError(), 5000);
      return () => clearTimeout(timer);
    }
  }, [error, clearError]);

  const handlePaymentMethodSelect = async (method: PaymentMethod) => {
    setSelectedMethod(method);
    
    if (method === 'bank_transfer') {
      // Bank transfer doesn't need payment intent
      setShowMethodSelection(false);
      return;
    }
    
    try {
      const intent = await initializePayment(amount, bookingId, method);
      setPaymentIntent(intent);
      setShowMethodSelection(false);
    } catch (err) {
      console.error('Payment initialization failed:', err);
    }
  };

  const handleStripeSuccess = async (paymentMethodId: string) => {
    try {
      await processPayment(paymentIntent, { paymentMethodId });
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Stripe payment failed:', err);
    }
  };

  const handlePayPalSuccess = async (orderId: string) => {
    try {
      await processPayment(paymentIntent, { orderId });
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('PayPal payment failed:', err);
    }
  };

  const handleBankTransferConfirm = () => {
    // For bank transfer, we just mark it as pending
    if (onSuccess) onSuccess();
  };

  const handleCancel = () => {
    setShowMethodSelection(true);
    setSelectedMethod(null);
    setPaymentIntent(null);
    if (onCancel) onCancel();
  };

  const handleError = (errorMessage: string) => {
    console.error('Payment error:', errorMessage);
    alert(`Zahlungsfehler: ${errorMessage}`);
  };

  const formatAmount = (cents: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(cents / 100);
  };

  return (
    <div className="payment-form">
      {error && (
        <div className="payment-error-banner">
          <span>⚠️ {error}</span>
        </div>
      )}

      {showMethodSelection ? (
        <div className="payment-methods">
          <div className="payment-header">
            <h2>Zahlungsmethode wählen</h2>
            <p className="payment-amount">
              Zu zahlender Betrag: <strong>{formatAmount(amount)}</strong>
            </p>
          </div>
          
          <div className="payment-method-cards">
            <div 
              className={`payment-method-card ${selectedMethod === 'stripe' ? 'selected' : ''}`}
              onClick={() => handlePaymentMethodSelect('stripe')}
            >
              <div className="method-icon">💳</div>
              <h4>Kreditkarte</h4>
              <p>Visa, Mastercard, American Express</p>
              <div className="method-badges">
                <span className="badge">Sofortzahlung</span>
                <span className="badge">3D Secure</span>
              </div>
            </div>

            <div 
              className={`payment-method-card ${selectedMethod === 'paypal' ? 'selected' : ''}`}
              onClick={() => handlePaymentMethodSelect('paypal')}
            >
              <div className="method-icon">
                <span style={{ color: '#003087', fontWeight: 'bold' }}>Pay</span>
                <span style={{ color: '#009cde', fontWeight: 'bold' }}>Pal</span>
              </div>
              <h4>PayPal</h4>
              <p>Schnell und sicher mit PayPal</p>
              <div className="method-badges">
                <span className="badge">Express Checkout</span>
                <span className="badge">Käuferschutz</span>
              </div>
            </div>

            <div 
              className={`payment-method-card ${selectedMethod === 'bank_transfer' ? 'selected' : ''}`}
              onClick={() => handlePaymentMethodSelect('bank_transfer')}
            >
              <div className="method-icon">🏦</div>
              <h4>Überweisung</h4>
              <p>Klassische Banküberweisung</p>
              <div className="method-badges">
                <span className="badge">SEPA</span>
                <span className="badge">3-5 Tage</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="payment-interface">
          {selectedMethod === 'stripe' && (
            <StripePayment
              clientSecret={paymentIntent?.clientSecret}
              amount={amount}
              bookingId={bookingId}
              onSuccess={handleStripeSuccess}
              onError={handleError}
              onCancel={handleCancel}
            />
          )}

          {selectedMethod === 'paypal' && (
            <PayPalPayment
              amount={amount}
              orderId={paymentIntent?.id}
              onSuccess={handlePayPalSuccess}
              onError={handleError}
              onCancel={handleCancel}
            />
          )}

          {selectedMethod === 'bank_transfer' && (
            <div className="bank-transfer-container">
              <div className="bank-header">
                <h3>🏦 Banküberweisung</h3>
                <button className="back-button" onClick={handleCancel}>
                  ← Zurück
                </button>
              </div>
              
              <div className="transfer-info-box">
                <h4>Überweisungsdaten</h4>
                <div className="transfer-details">
                  <div className="detail-row">
                    <span className="label">Empfänger:</span>
                    <span className="value">Frankenfloss GmbH</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">IBAN:</span>
                    <span className="value copy-able">DE89 3704 0044 0532 0130 00</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">BIC:</span>
                    <span className="value copy-able">COBADEFFXXX</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Verwendungszweck:</span>
                    <span className="value copy-able">Buchung #{bookingId}</span>
                  </div>
                  <div className="detail-row highlight">
                    <span className="label">Betrag:</span>
                    <span className="value amount">{formatAmount(amount)}</span>
                  </div>
                </div>
              </div>

              <div className="transfer-notice">
                <p>⏰ <strong>Wichtig:</strong> Bitte überweisen Sie den Betrag innerhalb von <strong>7 Tagen</strong>.</p>
                <p>📧 Sie erhalten eine E-Mail mit den Überweisungsdaten.</p>
              </div>

              <button 
                className="confirm-button"
                onClick={handleBankTransferConfirm}
              >
                Buchung bestätigen & Überweisungsdaten erhalten
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PaymentForm;
